import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AUTH_REQUIRED_MESSAGE } from '../utils/customerAuth';
import { devLog } from '../utils/devLog';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoading, user } = useAuth();
  const location = useLocation();

  const hasCustomerSession = (): boolean => {
    if (user) return true;
    try {
      const t = localStorage.getItem('token');
      const s = localStorage.getItem('user');
      if (!t || !s) return false;
      const u = JSON.parse(s) as { role?: string };
      return u?.role !== 'admin';
    } catch {
      return false;
    }
  };

  const customerAuthed = hasCustomerSession();

  devLog('[ProtectedRoute]', {
    pathname: location.pathname,
    customerAuthed,
    isLoading,
    hasUser: !!user,
  });

  // Show loading spinner while auth context is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Redirect to login if no customer session (ignore admin-only session for these pages)
  if (!customerAuthed) {
    devLog('[ProtectedRoute] Not authenticated, redirecting to login');
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location, authMessage: AUTH_REQUIRED_MESSAGE }}
      />
    );
  }

  devLog('[ProtectedRoute] Access granted');
  return children;
};

export default ProtectedRoute;
